/**
 * HSS_Core — Shared logic layer v1.8
 *
 * Changes in v1.8:
 *  - Added `sort` as a fifth, independent piece of state alongside
 *    year/model/trim/feature/keywords — backing the new [hss_video_sort]
 *    shortcode, which replaces FacetWP's own native Sort facet markup with
 *    HSS-owned markup that reads/writes through this same module instead of
 *    firing a separate facetwp-refresh that clobbered the other facets.
 *    Like feature, it's a plain value with no ancestry, tracked via
 *    `state.sort` (a raw string — the sort option's value, e.g. "post_date"
 *    or "title" — not an {slug,name} object, since sort options aren't a
 *    taxonomy) and its own URL param (config.paramSort, `_video_sort`).
 *    Unlike feature, it's NOT persisted to a cookie — sort is a page/listing
 *    preference, not a durable filter, so readCookieState() always reports
 *    it as null and it's only ever sourced from the current URL.
 *    parseUrlState(), resolveInitialState(), buildUrl(), navigate(), and
 *    buildClearFilterUrl() all now know about it, so every caller (chip bar
 *    Search, configurator Submit, both Clear Alls, and the sort control
 *    itself) goes through one canonical place instead of five copies of the
 *    same "don't drop the other four facets" logic. navigate()'s FWP.fetch()
 *    branch writes it to FWP.facets under config.sortFacetName — the literal
 *    FacetWP facet name (`video_sort`, no underscore), NOT config.paramSort
 *    (`_video_sort`, the URL var) — those are deliberately different strings
 *    tracked in two different config keys; see HSS_SORT_FACET_NAME's
 *    docblock in the main plugin file for why FacetWP itself draws that
 *    distinction.
 *
 * Changes in v1.7:
 *  - Split the single featureMatchingEnabled toggle into two independent
 *    ones: featureMatchingEnabled now only gates Smart Parse's typed-
 *    text-to-feature GUESS on Enter/Search, while the new
 *    featureDropdownEnabled gates feature_category showing up in the
 *    autocomplete dropdown at all (typed-text matches alongside Model/
 *    Trim results, AND the guided Feature Category step after
 *    Year+Model+Trim are picked). Lets the team test one in a staging
 *    environment without the other going live on prod. searchFlat()'s
 *    internal feature gate moved to featureDropdownEnabled too — it's
 *    dropdown-only (hss-frontend.js's _getResults() CASE 3), never
 *    called from Smart Parse, so it was mis-scoped under the old single
 *    toggle even though it didn't matter until the two could diverge.
 *
 * Changes in v1.6:
 *  - navigate()'s real-redirect fallback (window.location.href — the
 *    branch that runs whenever FWP isn't live on the current page,
 *    which is most of the time for this globally-placed bar/configurator)
 *    now shows a brief full-viewport loading overlay first
 *    (showLoadingOverlay/hideLoadingOverlay), so there's no dead-air gap
 *    between clicking Search/Select Your Vehicle and the results page
 *    finishing its load. The FWP.fetch() branch is untouched — that's
 *    an in-place AJAX update with no redirect to bridge.
 *
 * Changes in v1.5:
 *  - Fixed buildClearFilterUrl() leaving _keyword_search (and
 *    _feature_category) in the URL after Clear All. It was handing the
 *    *current* URL — still carrying every HSS facet param — straight to
 *    FWPVFM's ?_clear_vehicle_filter=1 endpoint and trusting FWPVFM to
 *    clean up the query string. FWPVFM only ever touches the vehicle-
 *    filter param it owns (paramModel); it has no concept of paramKeyword
 *    or paramFeature, so those rode along untouched and got reapplied on
 *    the very page load the redirect landed on. Now takes config and
 *    strips paramModel/paramKeyword/paramFeature itself before handing
 *    off, so Clear All doesn't depend on another plugin knowing about
 *    params it doesn't own. Callers (hss-frontend.js's _clearAll(),
 *    hss-configurator.js's Clear handler) updated to pass config through.
 *
 * Changes in v1.4:
 *  - Added an admin-managed term-corrections list (setTermCorrections /
 *    getTermCorrections / applyTermCorrections) — a whole-word/phrase,
 *    case-insensitive find/replace run against typed search text before
 *    Smart Parse extracts Year/Model/Trim/Feature from it, so a corrected
 *    term is recognised as a chip rather than falling through to a plain
 *    keyword search. Populated from Settings → Term Corrections via
 *    hssTermCorrections in hss-frontend.js.
 *
 * Changes in v1.3:
 *  - Added a feature-matching toggle (setFeatureMatchingEnabled /
 *    isFeatureMatchingEnabled) so free-text scoring against
 *    feature_category terms in searchFlat() can be switched off from
 *    Settings while feature_category tagging is still being cleaned up —
 *    typed queries then fall back to year/model/trim + keyword matching
 *    only. Direct links, the feature cookie, and the configurator's
 *    explicit Feature Category <select> are untouched by this toggle.
 *
 * Changes in v1.2:
 *  - Added a Feature Category cookie (syncFeatureCookie/readFeatureCookie/
 *    clearFeatureCookie) — entirely separate from FWPVFM's, which is
 *    deliberately year/model/trim only. readCookieState() now folds it in.
 *
 * Changes in v1.1:
 *  - Removed syncFWPVFMCookie()/clearFWPVFMCookie() — reviewed against
 *    FWP Vehicle Filter Memory's actual source and found it already syncs
 *    its own cookie (native 'facetwp-loaded' listener + DOMContentLoaded,
 *    both reading the URL our navigate() already updates) more robustly
 *    than we could duplicate, including a server-side homepage redirect
 *    we can't replicate client-side. The earlier "FacetWP only fires
 *    facetwp-loaded via jQuery" assumption behind those functions doesn't
 *    hold — FWPVFM listens with a plain addEventListener and works.
 *  - Added buildClearFilterUrl(), which points "Clear All" actions at
 *    FWPVFM's own ?_clear_vehicle_filter=1 handler instead.
 *  - readCookieState()/resolveInitialState() are unchanged and still the
 *    right call for globally-placed widgets — kept, not removed.
 *
 * Extracted out of hss-frontend.js so the chip search bar AND the visual
 * configurator (or any future UI) call one canonical implementation instead
 * of each maintaining its own copy of:
 *
 *   - flattening the Year → Model → Trim tree for lookups
 *   - fuzzy scoring / matching against tree nodes AND feature_category terms
 *   - building the results URL and pushing/syncing state to FacetWP
 *   - reading the current filter state back out of the URL
 *
 * Exposed as a single global, window.HSSCore, with no DOM dependencies of
 * its own — every function here is pure data in, data out, except
 * navigate() which is the one place that reaches out to FWP/history.
 *
 * feature_category is modeled as a fourth, independent facet. Unlike
 * year/model/trim it has no ancestry (it isn't nested under a model or
 * trim), so it's tracked separately as `state.feature` / `flat.features`
 * throughout, and its own URL param (paramFeature, default
 * `_feature_category`) never gets bundled into paramModel's comma-joined
 * slug string.
 */
window.HSSCore = (function () {
	'use strict';

	/* ── Flattening ──────────────────────────────────────────────────── */

	/**
	 * Flattens the nested Year → Model → Trim tree into parallel arrays for
	 * O(n) lookups by slug, and folds in the flat feature_category list
	 * alongside it. Returns (and internally caches) a single `flat` object:
	 *   { years: [...], models: [...], trims: [...], features: [...] }
	 *
	 * @param  {Array} tree               hssTree — from HSS_Taxonomy::get_tree()
	 * @param  {Array} [featureCategories] hssFeatureCategories — from HSS_Taxonomy::get_feature_categories()
	 * @return {Object} flat
	 */
	function buildFlat(tree, featureCategories) {
		flat.years = [];
		flat.models = [];
		flat.trims = [];
		flat.features = (featureCategories || window.hssFeatureCategories || []).slice();

		(tree || []).forEach(function (year) {
			flat.years.push(year);
			(year.models || []).forEach(function (model) {
				flat.models.push({
					id: model.id, name: model.name, slug: model.slug, type: 'model',
					yearId: model.yearId, yearSlug: year.slug, yearName: year.name,
					trims: model.trims || []
				});
				(model.trims || []).forEach(function (trim) {
					flat.trims.push({
						id: trim.id, name: trim.name, slug: trim.slug, type: 'trim',
						modelId: trim.modelId, modelSlug: model.slug, modelName: model.name,
						yearId: trim.yearId, yearSlug: year.slug, yearName: year.name
					});
				});
			});
		});

		return flat;
	}

	var flat = { years: [], models: [], trims: [], features: [] };

	function getFlat() { return flat; }

	/* ── Feature-matching toggle ─────────────────────────────────────────
	 * Two independent toggles, both admin-managed (Settings → Search
	 * Behaviour), split in v1.7 so the team can test one without the
	 * other going live:
	 *
	 *  - featureMatchingEnabled: gates Smart Parse's typed-text-to-
	 *    feature GUESS on Enter/Search (hss-frontend.js's
	 *    _smartParseAndSearch) — free text silently becoming a
	 *    feature_category filter on submit.
	 *  - featureDropdownEnabled: gates feature_category appearing in the
	 *    autocomplete DROPDOWN at all — both a typed query matching a
	 *    feature name alongside Model/Trim results, and the guided
	 *    next-step preview shown after Year+Model+Trim are picked (see
	 *    hss-frontend.js's _getResults()). This is the user explicitly
	 *    clicking an offered option, never text being reinterpreted, so
	 *    it was worth keeping separate from the guess-on-submit toggle
	 *    above rather than reusing it.
	 *
	 * Neither affects: direct/shared links (?_feature_category=slug),
	 * the FWPVFM-independent feature cookie, or the configurator's
	 * explicit Feature Category <select> — those are the user (or a
	 * link) stating a feature outright, not HSS guessing or offering
	 * one. Both default to true here; set from HSS_Admin's two settings
	 * via hssConfig.featureMatchingEnabled / .featureDropdownEnabled in
	 * hss-frontend.js.
	 */
	var featureMatchingEnabled = true;
	var featureDropdownEnabled = true;

	function setFeatureMatchingEnabled( enabled ) {
		featureMatchingEnabled = !! enabled;
	}

	function isFeatureMatchingEnabled() {
		return featureMatchingEnabled;
	}

	function setFeatureDropdownEnabled( enabled ) {
		featureDropdownEnabled = !! enabled;
	}

	function isFeatureDropdownEnabled() {
		return featureDropdownEnabled;
	}

	/* ── Term corrections ──────────────────────────────────────────────
	 * Admin-managed incorrect → correct term list (Settings → Term
	 * Corrections), applied to the raw typed string before Smart Parse
	 * runs its Year/Model/Trim/Feature extraction. This means a corrected
	 * term (e.g. "Elantera" → "Elantra") is recognised as a proper chip
	 * instead of falling through to a plain keyword search — the
	 * correction happens first, parsing happens on its output.
	 */
	var termCorrections = [];

	function setTermCorrections(pairs) {
		termCorrections = (pairs || []).filter(function (p) {
			return p && p.incorrect && p.correct;
		});
	}

	function getTermCorrections() {
		return termCorrections;
	}

	function escRegExp(str) {
		return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	}

	/**
	 * Replaces every whole-word/phrase occurrence of each incorrect term
	 * with its correct counterpart, case-insensitively, in a single pass
	 * over the original text. Longer incorrect terms are tried first (via
	 * regex alternation order) so a multi-word phrase (e.g. "santa fe")
	 * wins outright over a shorter entry sharing its prefix (e.g. "santa")
	 * instead of leaving a dangling word behind after a naive sequential
	 * replace.
	 *
	 * @param  {string} text
	 * @return {string}
	 */
	function applyTermCorrections(text) {
		if (!text || !termCorrections.length) return text;

		var sorted = termCorrections.slice().sort(function (a, b) {
			return b.incorrect.length - a.incorrect.length;
		});

		var lookup = {};
		var alternatives = sorted.map(function (pair) {
			lookup[pair.incorrect.toLowerCase()] = pair.correct;
			return escRegExp(pair.incorrect);
		});

		var re = new RegExp('\\b(' + alternatives.join('|') + ')\\b', 'gi');
		return text.replace(re, function (match) {
			return lookup[match.toLowerCase()] || match;
		});
	}

	/* ── Fuzzy scoring ───────────────────────────────────────────────── */

	function score(haystack, needle) {
		if (!needle) return 0;
		var h = String(haystack).toLowerCase();
		var n = String(needle).toLowerCase();
		if (h === n)            return 100;
		if (h.indexOf(n) === 0) return 85;
		if (h.indexOf(n) !== -1) return 65;
		if (fuzzySubseq(n, h))  return 30;
		return 0;
	}

	function fuzzySubseq(needle, haystack) {
		var ni = 0;
		for (var hi = 0; hi < haystack.length && ni < needle.length; hi++) {
			if (needle[ni] === haystack[hi]) ni++;
		}
		return ni === needle.length;
	}

	/**
	 * Full (unfiltered) flat search across years, models, trims, AND
	 * feature categories — used whenever there's no year/model context
	 * narrowing the result set already. This is purely dropdown/
	 * autocomplete rendering (the only caller is hss-frontend.js's
	 * _getResults(), CASE 3), never Smart Parse's on-submit guess — so
	 * its feature-category matching is gated by featureDropdownEnabled,
	 * not featureMatchingEnabled.
	 *
	 * @param  {string} query
	 * @return {{years:Array, models:Array, trims:Array, features:Array}}
	 */
	function searchFlat(query) {
		var q = (query || '').toLowerCase().trim();
		var results = { years: [], models: [], trims: [], features: [] };
		if (!q) return results;

		flat.years.forEach(function (y) {
			var s = score(y.name, q);
			if (s > 0) results.years.push(Object.assign({}, y, { _score: s }));
		});
		flat.models.forEach(function (m) {
			var s = score(m.name, q);
			if (s > 0) results.models.push(Object.assign({}, m, { _score: s }));
		});
		flat.trims.forEach(function (t) {
			var s = score(t.name, q);
			if (s > 0) results.trims.push(Object.assign({}, t, { _score: s }));
		});
		if (featureDropdownEnabled) {
			flat.features.forEach(function (f) {
				var s = score(f.name, q);
				if (s > 0) results.features.push(Object.assign({}, f, { _score: s }));
			});
		}

		['years', 'models', 'trims', 'features'].forEach(function (k) {
			results[k].sort(function (a, b) { return b._score - a._score; });
		});
		return results;
	}

	/* ── URL state ───────────────────────────────────────────────────── */

	/**
	 * Reads the current filter state back out of location.search.
	 * paramModel holds the comma-joined year,model,trim slug string (as
	 * before); paramFeature is read as its own independent single-value
	 * param, since feature_category isn't part of that ancestry chain.
	 *
	 * @param  {Object} config  { paramModel, paramKeyword, paramFeature, paramSort }
	 * @return {{year:?Object, model:?Object, trim:?Object, feature:?Object, keywords:string, sort:?string}}
	 */
	function parseUrlState(config) {
		var params = new URLSearchParams(window.location.search);
		var modelFilter = params.get(config.paramModel);
		var keyword     = params.get(config.paramKeyword);
		var featureSlug = config.paramFeature ? params.get(config.paramFeature) : null;
		var sortValue   = config.paramSort ? params.get(config.paramSort) : null;

		var state = { year: null, model: null, trim: null, feature: null, keywords: '', sort: null };

		if (modelFilter) {
			var parts = modelFilter.split(',');
			var y = flat.years.find(function (y) { return y.slug === parts[0]; });
			if (y) state.year = { slug: y.slug, name: y.name };
			var m = flat.models.find(function (m) { return m.slug === parts[1]; });
			if (m) state.model = { slug: m.slug, name: m.name };
			var t = flat.trims.find(function (t) { return t.slug === parts[2]; });
			if (t) state.trim = { slug: t.slug, name: t.name };
		}

		if (keyword) state.keywords = decodeURIComponent(keyword);

		if (featureSlug) {
			var f = flat.features.find(function (f) { return f.slug === featureSlug; });
			state.feature = f ? { slug: f.slug, name: f.name } : { slug: featureSlug, name: featureSlug };
		}

		// Not a taxonomy lookup — sort options are admin-defined label/value
		// pairs (Settings → Sort Options), not tree terms, so the raw value
		// is trusted as-is. An unrecognised value (a stale/hand-edited link)
		// still round-trips through buildUrl() correctly; it just won't
		// match any <option> in the [hss_video_sort] <select>.
		if (sortValue) state.sort = sortValue;

		return state;
	}

	/**
	 * Reads year/model/trim back out of the FWPVFM cookie (written by the
	 * FWP Vehicle Filter Memory plugin itself — see buildClearFilterUrl's
	 * note on why we don't write it ourselves). This is the fallback
	 * source of truth for widgets that appear globally — a header search
	 * bar or a feed on the homepage has no `_model_filter` in its own URL
	 * to read, but the cookie persists whatever was last selected on the
	 * results page.
	 *
	 * Feature Category comes from a second, separate cookie we own outright
	 * (see the Feature Category cookie section below) — FWPVFM's format is
	 * deliberately year/model/trim only, so persisting Feature the same way
	 * needs its own storage rather than changing another plugin's format.
	 * Keywords aren't persisted at all; a free-text search is a one-off,
	 * not a durable preference.
	 *
	 * Sort is deliberately excluded from this fallback (always null here) —
	 * see the v1.8 changelog note above: it's a page/listing preference, not
	 * a durable one, so it's only ever read from the current URL.
	 *
	 * @return {{year:?Object, model:?Object, trim:?Object, feature:?Object, keywords:'', sort:null}}
	 */
	function readCookieState() {
		var state = { year: null, model: null, trim: null, feature: null, keywords: '', sort: null };

		var cfg = window.fwpVehicleFilterMemory;
		if (cfg) {
			var cookieName = cfg.cookieName || 'fwp_vehicle_filter';
			var match = document.cookie.match(new RegExp('(?:^|; )' + cookieName + '=([^;]*)'));
			if (match) {
				var parts = decodeURIComponent(match[1]).split(',');
				var y = flat.years.find(function (y) { return y.slug === parts[0]; });
				if (y) state.year = { slug: y.slug, name: y.name };
				var m = flat.models.find(function (m) { return m.slug === parts[1]; });
				if (m) state.model = { slug: m.slug, name: m.name };
				var t = flat.trims.find(function (t) { return t.slug === parts[2]; });
				if (t) state.trim = { slug: t.slug, name: t.name };
			}
		}

		state.feature = readFeatureCookie();

		return state;
	}

	/* ── Feature Category cookie (ours, independent of FWPVFM) ──────────── */
	/**
	 * FWPVFM's cookie is deliberately year/model/trim only, so Feature
	 * Category needs its own persistence to survive across pages the same
	 * way. This cookie's name, format, and lifecycle are entirely ours —
	 * no risk of colliding with FWPVFM's cookie or its clear endpoint,
	 * which only ever touches its own. Same 60-day default as FWPVFM,
	 * borrowing its configured cookieDays when available just so the two
	 * don't drift into visibly different "remembered for how long" windows.
	 */
	var FEATURE_COOKIE_NAME = 'hss_feature_category';

	function readFeatureCookie() {
		var match = document.cookie.match(new RegExp('(?:^|; )' + FEATURE_COOKIE_NAME + '=([^;]*)'));
		if (!match) return null;
		var slug = decodeURIComponent(match[1]);
		if (!slug) return null;
		var f = flat.features.find(function (f) { return f.slug === slug; });
		return f ? { slug: f.slug, name: f.name } : { slug: slug, name: slug };
	}

	/**
	 * Writes or clears the Feature Category cookie based on state.feature.
	 * Call this any time feature selection changes — chip add/remove,
	 * Configurator submit, Clear All, etc.
	 *
	 * @param {Object} state  Only state.feature is read: {slug,name}|null
	 */
	function syncFeatureCookie(state) {
		if (state && state.feature && state.feature.slug) {
			var cfg  = window.fwpVehicleFilterMemory;
			var days = (cfg && parseInt(cfg.cookieDays, 10)) || 60;
			var maxAge = days * 24 * 60 * 60;
			document.cookie = FEATURE_COOKIE_NAME + '=' + encodeURIComponent(state.feature.slug) +
				'; path=/; max-age=' + maxAge + '; SameSite=Lax';
		} else {
			clearFeatureCookie();
		}
	}

	function clearFeatureCookie() {
		document.cookie = FEATURE_COOKIE_NAME + '=; path=/; max-age=0; SameSite=Lax';
	}

	/**
	 * The one function global widgets (header chip bar, homepage feed)
	 * should call on init instead of parseUrlState() directly.
	 *
	 * Priority: URL params win whenever any are present — this covers the
	 * actual results page (where FacetWP needs real query params anyway)
	 * and any deep-linked/shared URL, which should always show exactly
	 * what it says rather than being silently overridden by an old cookie.
	 * Falls back to the FWPVFM cookie only when the URL has nothing at all,
	 * which is every other page on the site.
	 *
	 * @param  {Object} config
	 * @return {{year:?Object, model:?Object, trim:?Object, feature:?Object, keywords:string, sort:?string}}
	 */
	function resolveInitialState(config) {
		var urlState = parseUrlState(config);
		var hasUrlState = !!(urlState.year || urlState.model || urlState.trim ||
			urlState.feature || urlState.keywords || urlState.sort);
		return hasUrlState ? urlState : readCookieState();
	}

	/**
	 * Builds the results URL for a given state — same shape as the live
	 * example: ?_model_filter=2026,elantra-2026&_feature_category=driving
	 *
	 * @param  {Object} config
	 * @param  {Object} state
	 * @return {string}
	 */
	function buildUrl(config, state) {
		var base   = config.searchPage || window.location.pathname;
		var params = new URLSearchParams();
		var parts  = [];

		if (state.year)  parts.push(state.year.slug);
		if (state.model) parts.push(state.model.slug);
		if (state.trim)  parts.push(state.trim.slug);

		if (parts.length)     params.set(config.paramModel, parts.join(','));
		if (state.keywords)   params.set(config.paramKeyword, state.keywords);
		if (state.feature && config.paramFeature) {
			params.set(config.paramFeature, state.feature.slug);
		}
		if (state.sort && config.paramSort) {
			params.set(config.paramSort, state.sort);
		}

		var qs = params.toString();
		return base + (qs ? '?' + qs : '');
	}

	/* ── Loading overlay ──────────────────────────────────────────────
	 * Bridges the dead-air gap between clicking Search (or the
	 * configurator's Select Your Vehicle button) and the results page
	 * actually finishing its load. Only relevant to navigate()'s real
	 * window.location.href fallback — the FWP.fetch() branch updates
	 * the current page via AJAX with no redirect to bridge, so it never
	 * shows this. Self-contained: injects its own <style> once rather
	 * than depending on hss-frontend.css or hss-configurator.css being
	 * loaded, since either widget can trigger navigate() independently
	 * and only one of those stylesheets may be present on a given page.
	 */
	var overlayStyleInjected = false;

	function injectOverlayStyle() {
		if (overlayStyleInjected) return;
		overlayStyleInjected = true;
		var style = document.createElement('style');
		style.id = 'hss-loading-overlay-style';
		style.textContent =
			'.hss-loading-overlay{position:fixed;inset:0;z-index:999999;display:flex;' +
			'flex-direction:column;align-items:center;justify-content:center;gap:14px;' +
			'background:rgba(255,255,255,.92);opacity:0;transition:opacity .15s ease;}' +
			'.hss-loading-overlay.is-visible{opacity:1;}' +
			'.hss-loading-spinner{width:40px;height:40px;border-radius:50%;' +
			'border:4px solid var(--hss-border-default,#d0d5dd);' +
			'border-top-color:var(--hss-btn-focus,#00AAD2);animation:hss-spin .8s linear infinite;}' +
			'.hss-loading-text{font:600 14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;' +
			'color:var(--hss-btn-default,#002C5F);}' +
			'@keyframes hss-spin{to{transform:rotate(360deg);}}';
		document.head.appendChild(style);
	}

	/**
	 * Shows a brief full-viewport loading overlay. Call immediately
	 * before a real page navigation (window.location.href = …) — never
	 * before FWP.fetch(), which has no redirect to bridge.
	 *
	 * No matching hideLoadingOverlay() call is needed in the normal
	 * case: the browser is about to unload this page for the results
	 * page, tearing the overlay down along with everything else.
	 * hideLoadingOverlay() exists only for a caller that shows it but
	 * then aborts before actually navigating.
	 *
	 * @param {string} [text]  Defaults to 'Searching…'
	 */
	function showLoadingOverlay(text) {
		injectOverlayStyle();
		hideLoadingOverlay(); // guard against a stray duplicate
		var el = document.createElement('div');
		el.className = 'hss-loading-overlay';
		el.id = 'hss-loading-overlay';
		el.setAttribute('role', 'status');
		el.setAttribute('aria-live', 'polite');
		el.innerHTML = '<div class="hss-loading-spinner"></div><div class="hss-loading-text">' +
			escHtml(text || 'Searching…') + '</div>';
		document.body.appendChild(el);
		// Next frame, so the opacity transition actually animates in
		// rather than snapping straight to visible.
		requestAnimationFrame(function () { el.classList.add('is-visible'); });
	}

	function hideLoadingOverlay() {
		var el = document.getElementById('hss-loading-overlay');
		if (el && el.parentNode) el.parentNode.removeChild(el);
	}

	/**
	 * The one canonical way anything in this plugin talks to FacetWP.
	 * Sets FWP.facets for paramModel/paramKeyword (as before), when
	 * config.paramFeature is set, its own independent feature_category
	 * facet, and — same idea — when config.sortFacetName is set, the Sort
	 * facet too, keyed by the literal FacetWP facet name (`video_sort`,
	 * no underscore), not config.paramSort (`_video_sort`, the URL var
	 * buildUrl() below uses) — then fetches and syncs the URL. Falls back
	 * to a hard redirect when FWP isn't present on the page.
	 *
	 * Every caller (chip bar Search/Clear All, configurator Submit/Clear
	 * All, [hss_video_sort] itself) always passes its FULL current state —
	 * not just the one facet it's changing — so this always re-asserts
	 * every HSS-owned facet on every call. That's what makes it safe for,
	 * say, the sort control to change sort without clobbering an active
	 * Year/Model chip, or Search to submit a chip change without dropping
	 * whatever sort was already applied.
	 *
	 * @param {Object} config
	 * @param {Object} state
	 */
	function navigate(config, state) {
		var parts = [];
		if (state.year)  parts.push(state.year.slug);
		if (state.model) parts.push(state.model.slug);
		if (state.trim)  parts.push(state.trim.slug);

		if (typeof window.FWP !== 'undefined' && typeof FWP.fetch === 'function') {
			FWP.facets[config.paramModel]   = parts;
			FWP.facets[config.paramKeyword] = state.keywords ? [state.keywords] : [];
			if (config.paramFeature) {
				FWP.facets[config.paramFeature] = state.feature ? [state.feature.slug] : [];
			}
			if (config.sortFacetName) {
				FWP.facets[config.sortFacetName] = state.sort ? [state.sort] : [];
			}
			FWP.fetch();
			try { window.history.pushState({}, '', buildUrl(config, state)); } catch (e) {}
			return;
		}

		showLoadingOverlay();
		window.location.href = buildUrl(config, state);
	}

	/**
	 * Builds a URL that hands cookie-clearing off to FWPVFM's own
	 * server-side handler (?_clear_vehicle_filter=1 on template_redirect)
	 * instead of us deleting the cookie client-side ourselves.
	 *
	 * We used to write/clear the FWPVFM cookie directly from here
	 * (syncFWPVFMCookie/clearFWPVFMCookie, removed once we could see
	 * FWPVFM's actual source). Turns out that was solving a problem FWPVFM
	 * already solves better on its own: it listens for 'facetwp-loaded'
	 * with a *native* addEventListener — not jQuery, despite what earlier
	 * comments here assumed — and re-syncs the cookie from the URL on
	 * every load and every FacetWP refresh. Since our own navigate()
	 * always updates the URL (pushState or a full redirect) before that
	 * event fires, FWPVFM was already catching every state change we'd
	 * have written ourselves. Two systems writing the same cookie is a
	 * race waiting to happen, not a safety net, so this now just points
	 * at FWPVFM's own endpoint rather than duplicating its logic.
	 *
	 * Reads clearParam from window.fwpVehicleFilterMemory (added in
	 * FWPVFM 1.0.1) rather than hardcoding another plugin's constant.
	 * Falls back to null if FWPVFM isn't active, so callers can tell the
	 * difference between "go clear it" and "nothing to clear."
	 *
	 * FWPVFM's endpoint only ever cleans up the vehicle-filter cookie/param
	 * it owns (paramModel) — it has no idea paramKeyword, paramFeature, or
	 * paramSort exist, so left to its own devices it would clear
	 * _model_filter and silently leave _keyword_search (and
	 * _feature_category, and _video_sort) sitting in the URL to get
	 * reapplied on the very page load this redirect lands on. So we strip
	 * all four of HSS's own facet params from targetUrl ourselves before
	 * handing off — Clear All shouldn't depend on another plugin knowing
	 * about params it doesn't own.
	 *
	 * @param  {Object} config     { paramModel, paramKeyword, paramFeature, paramSort }
	 * @param  {string} targetUrl  Page to land on after the cookie's cleared
	 * @return {?string}
	 */
	function buildClearFilterUrl(config, targetUrl) {
		var cfg = window.fwpVehicleFilterMemory;
		if (!cfg || !cfg.clearParam) return null;

		var url = new URL(targetUrl, window.location.href);
		if (config) {
			if (config.paramModel)   url.searchParams.delete(config.paramModel);
			if (config.paramKeyword) url.searchParams.delete(config.paramKeyword);
			if (config.paramFeature) url.searchParams.delete(config.paramFeature);
			if (config.paramSort)    url.searchParams.delete(config.paramSort);
		}
		url.searchParams.set(cfg.clearParam, '1');
		return url.toString();
	}

	/* ── Misc shared helpers ─────────────────────────────────────────── */

	function escHtml(str) {
		return String(str || '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	function highlight(text, query) {
		if (!query) return escHtml(text);
		var safe = String(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		return escHtml(text).replace(new RegExp('(' + safe + ')', 'gi'), '<mark>$1</mark>');
	}

	return {
		buildFlat: buildFlat,
		getFlat: getFlat,
		score: score,
		fuzzySubseq: fuzzySubseq,
		searchFlat: searchFlat,
		parseUrlState: parseUrlState,
		readCookieState: readCookieState,
		resolveInitialState: resolveInitialState,
		buildUrl: buildUrl,
		navigate: navigate,
		buildClearFilterUrl: buildClearFilterUrl,
		syncFeatureCookie: syncFeatureCookie,
		clearFeatureCookie: clearFeatureCookie,
		setFeatureMatchingEnabled: setFeatureMatchingEnabled,
		isFeatureMatchingEnabled: isFeatureMatchingEnabled,
		setFeatureDropdownEnabled: setFeatureDropdownEnabled,
		isFeatureDropdownEnabled: isFeatureDropdownEnabled,
		setTermCorrections: setTermCorrections,
		getTermCorrections: getTermCorrections,
		applyTermCorrections: applyTermCorrections,
		showLoadingOverlay: showLoadingOverlay,
		hideLoadingOverlay: hideLoadingOverlay,
		escHtml: escHtml,
		highlight: highlight
	};
}());
